import AbstractShape, {Dimensions, DimensionProperties} from "./AbstractShape";
import Length from "../../../../source/js/shared/models/Geometry/Length";
import isUndefined from "lodash/isUndefined";
import DimensionLine from "@/Models/Lines/DimensionLine";
import OffsetLine from "../Lines/OffsetLine";
import GuideLine from "../Lines/GuideLine";

interface TrapezoidPoint {
	x: number;
	y: number;
}

interface TrapezoidOffset {
	length: number;
	line: TrapezoidPoint;
	x: number;
	y: number;
}

export interface TrapezoidPosition {
	top: TrapezoidPoint;
	bottom: TrapezoidPoint;
	height: TrapezoidPoint;
	offset: TrapezoidOffset;
}

export class Trapezoid extends AbstractShape {
	constructor( dimensions?: object, modifiers?: object ) {
		super( 'trapezoid', dimensions, modifiers );
	}

	getHeight(): Length {
		return this.cleanedPrimitives.height;
	}

	getLength(): Length {
		return this.cleanedPrimitives.height;
	}

	getWidth(): Length {
		return this.getWidthBottom().max( this.getWidthTop() );
	}

	getWidthTop(): Length {
		return this.cleanedPrimitives.width_top;
	}

	getWidthBottom(): Length {
		return this.cleanedPrimitives.width_bottom;
	}

	getOffsetLeft(): Length {
		return this.cleanedPrimitives.offset_left;
	}

	getDimensionProperties(): DimensionProperties {
		return { primary: 'width_top', secondary: 'height', tertiary: 'width_bottom', quaternary: 'offset_left' };
	}

	getDimensionLines(ratio = 1, focusedElement = '', forceRender = false): DimensionLine[] {
		const height_horizontal_position = this.getWidthBottom().max( this.getWidthTop() );

		let top_starting_x_position = this.getOffsetLeft().abs();
		let bottom_starting_x_position = new Length( 0 );

		// If top is wider, we need to adjust the x position of the top and bottom line.
		if ( this.getWidthTop().in_mm() > this.getWidthBottom().in_mm() ) {
			top_starting_x_position = new Length( 0 );
			bottom_starting_x_position = this.getOffsetLeft().abs();
		}

		// The width of the line changes depending on the starting position.
		const actual_width_top = this.getWidthTop().add(top_starting_x_position);
		const actual_width_bottom = this.getWidthBottom().add(bottom_starting_x_position);

		const allLines =  [
			new DimensionLine( [height_horizontal_position.in_mm(), 0], [height_horizontal_position.in_mm(), this.getLength().in_mm()], this.renderDimensionLabel( 'height' ), [40, 0], ratio, this.getDimensionLineType('measurements-trapezoid-height', focusedElement, forceRender) ), // Vertical line
			new DimensionLine( [top_starting_x_position.in_mm(), this.getLength().in_mm()], [actual_width_top.in_mm(), this.getLength().in_mm()], this.renderDimensionLabel( 'width_top' ), [0, 40], ratio, this.getDimensionLineType('measurements-trapezoid-width_top', focusedElement, forceRender) ), // Horizontal line
			new DimensionLine( [bottom_starting_x_position.in_mm(), 0], [actual_width_bottom.in_mm(), 0], this.renderDimensionLabel( 'width_bottom' ), [0, -40], ratio, this.getDimensionLineType('measurements-trapezoid-width_bottom', focusedElement, forceRender) ), // Horizontal line
		];

		const widest_width = this.getWidthBottom().max( this.getWidthTop() );
		const smallest_width = this.getWidthBottom().min( this.getWidthTop() );

		const left_offset = ( widest_width.in_mm() - smallest_width.in_mm() ) / 2;

		if(left_offset === 0) {
			return allLines;
		}

		const left_point = [0, this.getHeight().in_mm() / 2];
		const right_point = [left_offset, this.getHeight().in_mm() / 2];

		allLines.push(new DimensionLine( left_point, right_point, this.renderDimensionLabel( 'offset_left' ), [0, 0], ratio ));

		if(focusedElement.includes("cutout_measurement") || forceRender) {
			return allLines;
		}

		return [];
	}

	getDefaultDimensions(): Dimensions {
		return {
			width_top: 20,
			height: 30,
			width_bottom: 50,
			offset_left: 15,
		};
	}

	getGuideLines(focusedElement = ''): GuideLine[] {
		const widest_width = this.getWidthBottom().max( this.getWidthTop() );
		const smallest_width = this.getWidthBottom().min( this.getWidthTop() );

		const left_offset = ( widest_width.in_mm() - smallest_width.in_mm() ) / 2;

		if( left_offset === 0 ) { // Don't show guide lines if the shape is a rectangle.
			return [];
		}

		const bottom_point = [left_offset, 0];
		const top_point = [left_offset, this.getHeight().in_mm()];
		return [
			new GuideLine( bottom_point, top_point )
		];
	}

	getDefaultCutoutDimensions(parentShape: AbstractShape): Dimensions {
		const enclosingRectangle = parentShape.getEnclosingRectangle();
		const width = enclosingRectangle.width();
		const height = enclosingRectangle.height();

		const width_top = width.divide(4).in_cm();
		const width_bottom = width_top + ( width_top / 2 );

		return {
			width_top: width_top,
			height: height.divide(2).in_cm(),
			width_bottom: width_bottom,
			offset_left: ( ( width_bottom - width_top ) / 2 ),
		};
	}

	getHiddenDimensions(): string[] {
		return ['offset_left'];
	}
}
